"use client";
import {
  Box,
  BoxProps,
  ButtonGroup,
  Flex,
  Heading,
  Text,
} from "@chakra-ui/react";
import { PageHero } from "lib/gcms";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useRef } from "react";
import { useResizeDetector } from "react-resize-detector";
import { AnimatedCarrotProps } from "./carat-down";
import { mapButton } from "utils/map-button";
import { SectionTemplate } from "../blocks";
import { getAlign } from "../blocks/section";

export interface HeroProps extends PageHero {
  home: boolean;
}

const CaratDown = dynamic(() => import("./carat-down"));

export function Hero({
  title,
  subtitle,
  image,
  buttons,
  home = false,
  bg,
  color,
  slug,
  textAlignment,
  contentPosition,
}: HeroProps) {
  const postHeroSection = useRef(null);
  const { height: heroContentH, ref: heroContentRef } = useResizeDetector();
  const onClickArrow = home
    ? () =>
        window.scrollTo({
          // Subtract 30 so that the user still sees the hero above
          //@ts-ignore
          top: postHeroSection.current.offsetTop - 30,
          behavior: "smooth",
        })
    : undefined;

  const carrotStyle = {
    onClick: onClickArrow,
    boxSize: "30px",
    color: "gray.400",
    cursor: "pointer",
  };
  const [startColor, endColor] = [bg, `${bg?.split(".")?.[0]}.50`];
  return (
    <>
      <SectionTemplate
        {...{ color }}
        bgGradient={
          slug === "about"
            ? undefined
            : `linear(to-r, ${startColor}, ${endColor})`
        }
        bg={slug === "about" ? bg : undefined}
        borderRadius="none"
        // Hero will resize to screen height, but not less than inner hero content
        {...(home && {
          py: { base: "50px", md: "50px" },
          h: {
            base: `max(${(heroContentH || 0) + 100}px, calc(100vh - 200px))`,
            md: `max(${(heroContentH || 0) + 100}px, calc(100vh - 72px))`,
          },
          display: "flex",
          flexDirection: "row",
          justifyContent: "stretch",
          alignItems: "center",
          innerProps: {
            w: "full",
          },
        })}
        py={slug === "about" ? { base: 16, lg: 0 } : undefined}
      >
        <Flex
          ref={heroContentRef}
          align="center"
          direction={{ base: "column-reverse", lg: "row" }}
          justify={{
            base: "center",
            lg: contentPosition ?? "space-between",
          }}
        >
          <Flex
            flex="1"
            width={{ lg: "fit-content" }}
            maxW={{ lg: "2xl" }}
            textAlign={{
              base: "center",
              lg: textAlignment ? getAlign(textAlignment) : "left",
            }}
            alignSelf={{
              base: "center",
              lg: "center",
            }}
            py={{
              base: "0",
              lg: "24",
            }}
            flexDir={"column"}
            align={"center"}
          >
            <Box
              pl={{ base: "0", lg: "20px" }}
              mt={{ base: slug === "about" ? "0" : "32px", lg: "0" }}
              gap="20px"
              lineHeight="2.5"
            >
              <Heading
                as="h1"
                fontWeight="bold"
                display={{
                  base: "contents",
                }}
                fontSize={{
                  base: 26,
                  md: 34,
                  lg: 38,
                  xl: 45,
                }}
                mb="20"
              >
                {title}
              </Heading>

              {subtitle && (
                <Text
                  mt={{ base: "2", lg: "4" }}
                  fontSize={{
                    lg: "lg",
                  }}
                  fontWeight="normal"
                  lineHeight={"tall"}
                >
                  {subtitle}
                </Text>
              )}
            </Box>
            {buttons && buttons.length > 0 && (
              <ButtonGroup mt={5}>
                {buttons?.map((button, idx) =>
                  mapButton(button, {
                    my: "2",
                    key: idx,
                  }),
                )}
              </ButtonGroup>
            )}
          </Flex>

          {image && (
            <Box
              w={{
                md: "80%",
                lg: "49%",
              }}
              mt={{
                base: 10,
                md: 0,
              }}
            >
              <Image
                {...image}
                priority
                width={750}
                height={500}
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 372px"
                src={image.url}
                alt={image.alt ?? image.handle}
                blurDataURL={
                  typeof image?.blurDataURL === "string"
                    ? image?.blurDataURL
                    : undefined
                }
                placeholder={
                  typeof image?.blurDataURL === "string" ? "blur" : undefined
                }
              />
            </Box>
          )}
        </Flex>
        {home && (
          <AbsPosCenteredAnimatedCarrot
            display={{ base: "none", lg: "block" }}
            top={{ lg: "-30px", xl: "-60px" }}
            yMovement={15}
            {...carrotStyle}
          />
        )}
      </SectionTemplate>

      {onClickArrow && <div ref={postHeroSection} />}
    </>
  );
}

const AbsPosCenteredAnimatedCarrot = ({
  display,
  top,
  yMovement,
  ...carrotStyle
}: Pick<BoxProps, "display" | "top"> & AnimatedCarrotProps) => {
  return (
    <Box position="relative" display={display}>
      <Box
        position="absolute"
        top={top}
        left="50%"
        transform="translate(-50%, 0)"
      >
        <CaratDown {...{ yMovement, ...carrotStyle }} />
      </Box>
    </Box>
  );
};

export default Hero;
